import React from "react";
import LegalPageLayout from "@/components/site/LegalPageLayout";

const Section = ({ n, title, children }) => (
  <section className="mt-8">
    <h2 className="font-heading text-xl font-bold text-slate-950 mb-3">
      {n}. {title}
    </h2>
    <div className="space-y-3 text-sm sm:text-base">{children}</div>
  </section>
);

export default function Terms() {
  return (
    <LegalPageLayout
      testId="terms-page"
      title="Terms & Conditions"
      subtitle="These Terms & Conditions govern the use of Rana Associates' property valuation coordination platform by banks, NBFCs, office staff, managers and field executives operating across Rajasthan."
      lastUpdated="22 February 2026"
    >
      <Section n="1" title="Definitions">
        <p><b>Platform</b> — the Rana Associates web application accessible at ranaassociate.com and associated dashboards.</p>
        <p><b>Bank / Lender</b> — a scheduled commercial bank, cooperative bank, housing finance company or NBFC registered on the Platform.</p>
        <p><b>Field Executive</b> — a professionally engaged individual who conducts on-site property inspections and prepares valuation inputs.</p>
        <p><b>Manager</b> — a territory manager responsible for approving and supervising Field Executives in one or more pincodes.</p>
        <p><b>Report</b> — a property valuation report generated from lender inputs, executive site visit data and supporting evidence.</p>
      </Section>

      <Section n="2" title="Services">
        <p>Rana Associates coordinates the end-to-end property valuation workflow — from lead generation to territory routing, on-site visit, photo evidence collection, report submission and payout of executives.</p>
        <p>We do not act as a bank, RBI-regulated valuer or credit-decision entity. Valuation outputs are advisory inputs prepared for the requesting bank's use.</p>
      </Section>

      <Section n="3" title="Eligibility & Account Registration">
        <p>Banks must be duly incorporated and provide accurate contact, agreement and portal-integration details. Field Executives must be 18+, provide valid KYC (Aadhaar & PAN) and complete a manager-verified approval process before accepting leads.</p>
        <p>You are responsible for maintaining the confidentiality of your account credentials. Suspicious activity should be reported to <a href="mailto:support@ranaassociate.com" className="text-amber-700 underline">support@ranaassociate.com</a> immediately.</p>
      </Section>

      <Section n="4" title="Commercial Terms">
        <p>Per-report fees are agreed on the Bank Agreement and are set as per the prevailing market standard for standard residential valuations. Commercial and high-value properties may attract additional fees as mutually agreed.</p>
        <p>Invoicing occurs on a periodic basis (usually monthly). Payment is due within 15 days of the invoice date. Delayed payments may attract interest at 1.5% per month.</p>
        <p>Field Executive payouts are computed at the agreed per-report rate (default ₹1,750) and disbursed on the same cycle post-report acceptance by the bank.</p>
      </Section>

      <Section n="5" title="Field Executive Conduct">
        <p>Field Executives shall (a) visit the property within the assigned TAT, (b) capture photographic evidence including boundaries and dimensions, (c) record bylaw compliance notes, and (d) submit accurate valuation inputs. Fraud, misrepresentation or missed visits will result in de-listing and forfeiture of pending payouts.</p>
      </Section>

      <Section n="6" title="Bank / Lender Obligations">
        <p>Banks shall provide accurate property details, lead notifications, portal-submission credentials (if applicable) and timely acceptance/rejection of submitted reports. Feedback must be provided within 5 business days of submission; else the report is deemed accepted.</p>
      </Section>

      <Section n="7" title="Intellectual Property">
        <p>All software, dashboards, workflow logic and brand assets on the Platform are owned by Rana Associates or its licensors. Users receive a limited, non-transferable, revocable licence to use the Platform for the intended purpose.</p>
      </Section>

      <Section n="8" title="Confidentiality & Data">
        <p>All property, borrower, KYC and financial data uploaded to the Platform is treated as confidential and processed strictly in accordance with our <a href="/privacy" className="text-amber-700 underline">Privacy Policy</a>. Field Executives and Managers agree not to share such data outside the Platform.</p>
      </Section>

      <Section n="9" title="Limitation of Liability">
        <p>Rana Associates' aggregate liability under this agreement for any bank/lender is limited to the total fees paid by that bank in the preceding three (3) months. We are not liable for indirect, incidental or consequential damages including loss of profit.</p>
      </Section>

      <Section n="10" title="Term & Termination">
        <p>Either party may terminate the agreement with thirty (30) days' written notice. Rana Associates may suspend accounts immediately for fraud, non-payment or breach of these Terms.</p>
      </Section>

      <Section n="11" title="Governing Law & Jurisdiction">
        <p>These Terms are governed by the laws of India. Disputes are subject to the exclusive jurisdiction of the courts of Jaipur, Rajasthan.</p>
      </Section>

      <Section n="12" title="Contact">
        <p>Questions about these Terms? Write to <a href="mailto:legal@ranaassociate.com" className="text-amber-700 underline">legal@ranaassociate.com</a>.</p>
      </Section>
    </LegalPageLayout>
  );
}
